import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  HostBinding,
  input,
  numberAttribute,
  OnChanges,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { SelectedPrice } from '@product/const';
import { OnlyNumberDirective } from '@sdvor/shared/ui';
import { SdvorIconComponent, SdvorLoaderComponent } from '@sdvor/shared/ui/v2';

@Component({
  selector: 'sd-product-item-counter',
  templateUrl: './product-item-counter.component.html',
  styleUrls: ['./product-item-counter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, OnlyNumberDirective, SdvorIconComponent, SdvorLoaderComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductItemCounterComponent),
      multi: true,
    },
  ],
})
export class ProductItemCounterComponent implements OnChanges, ControlValueAccessor, AfterViewInit {
  selectedPrice = input.required<SelectedPrice>();

  protected loading = input(false, { transform: booleanAttribute });
  protected allowZero = input(false, { transform: booleanAttribute });
  /**
   * Когда инпут в фокусе, а пользователь вводит количество товара, может возникать ситуация, когда введенное значение
   * затирается значением из стора. Это происходит из-за того, что при каждом изменении количества товара в сторе
   * происходит синхронизация с компонентом и если пользователь вводит значение чуть медленнее чем дебаунс, то запросы
   * на изменение количества идут друг за другом и последнее введенное значение затирается предыдущим.
   *
   * Чтобы решить эту проблему, была реализована логика, если параметр zeroFocusEmitValue установлен в true. В этом
   * случае, если пользователь вводит количество товара и инпут находится в фокусе, то setValue не будет вызываться,
   * что позволит избежать затирания введенного значения значением из стора.
   *
   * Если же параметр zeroFocusEmitValue установлен в false, то при каждом изменении количества товара будет происходить
   * синхронизация с компонентом и setValue будет вызываться при каждом изменении количества, даже если инпут находится
   * в фокусе.
   */
  protected zeroFocusEmitValue = input(true, { transform: booleanAttribute });
  protected readonly = input(false, { transform: booleanAttribute });
  protected isStepCount = input(false, { transform: booleanAttribute });
  protected showSaleUnitInput = input(false, {
    // eslint-disable-next-line @angular-eslint/no-input-rename
    alias: 'showSaleUnit',
    transform: booleanAttribute,
  });
  protected isGift = input(false, { transform: booleanAttribute });
  protected saleQuantity = input<number | undefined | null>(undefined);
  protected max = input(1000000000, { transform: numberAttribute });
  protected valueExceeded = signal(false);

  protected unit = computed(() => this.selectedPrice()?.unit);
  protected saleUnit = computed(() => this.selectedPrice()?.saleUnit);

  protected selectedUnitIsSaleUnit = computed(() => {
    const unitCode = this.unit()?.code;
    const saleCode = this.saleUnit()?.code;

    return Boolean(unitCode && saleCode && unitCode === saleCode);
  });

  protected computedSaleQuantity = computed(() => {
    if (this.saleQuantity() !== undefined && this.saleQuantity() !== null) {
      return this.saleQuantity();
    }

    const currentValue = this.currentValueSignal();
    const saleUnitRatio = this.saleUnit().ratio;

    if (saleUnitRatio !== undefined && saleUnitRatio !== null) {
      const result = currentValue * saleUnitRatio;

      if (Number.isInteger(result)) {
        return result;
      }

      const roundedRatio = Math.round(saleUnitRatio);

      return currentValue * roundedRatio;
    }

    return null;
  });

  protected min = computed(() => this.selectedPrice()?.orderMinimum ?? 1);
  protected step = computed(() => this.selectedPrice()?.multiplicity ?? 1);

  protected showSaleUnit = computed(() => {
    return this.showSaleUnitInput() && !this.selectedUnitIsSaleUnit();
  });

  protected quantityControl = new FormControl<number | null>(null);
  protected disabled = false;

  @ViewChild('quantityInput') quantityInput!: ElementRef<HTMLInputElement>;

  @HostBinding('class.readonly')
  protected get isReadonly(): boolean {
    return this.readonly();
  }

  protected get value(): number {
    return this.quantityControl.value ?? 0;
  }

  protected currentValueSignal = signal<number>(0);
  private isInputFocused = signal(false);

  /**
   * Cинхронизация контрола с состоянием приложения реализована через таймаут.
   * Если достаточно часто изменять количество товара, но чуть медленнее чем дебаунс,
   * так чтобы запросы шли друг за другом, происходит затирание последнего введенного
   * значения состоянием из стора.
   *
   * Чтобы решить эту проблему реализована логика:
   * После того как пользователь изменил значение в счетчике, мы ожидаем <syncTimeout> мс.,
   * и только после этого проверяем совпадает ли значение счетчика со значением в сторе,
   * если не совпадает, то синхронизируем значение счетчика.
   */
  private syncTimeout = 3000;
  private syncTimeoutId?: NodeJS.Timeout;
  private syncValue?: number;

  constructor(
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedPrice'] &&
      changes['selectedPrice'].currentValue &&
      changes['selectedPrice'].previousValue &&
      changes['selectedPrice'].currentValue.unit.code !== changes['selectedPrice'].previousValue.unit.code
    ) {
      const newValue = this.min() * this.step();
      this.valueUpdate(newValue);
    }
  }

  ngAfterViewInit(): void {
    this.quantityControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const newValue = value ?? 0;
      this.valueUpdate(newValue);
    });
  }

  writeValue(value: number): void {
    if (this.isInputFocused()) return;

    if (this.syncTimeoutId) {
      this.syncValue = value;

      return;
    }

    this.valueUpdate(value, false);
  }

  setSyncTimeout(): void {
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }

    this.syncTimeoutId = setTimeout(() => {
      this.syncTimeoutId = undefined;

      if (this.syncValue && this.value !== this.syncValue) {
        this.setValue(this.syncValue, false);
        this.syncValue = undefined;
      }
    }, this.syncTimeout);
  }

  registerOnChange(fn: (_: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  increment(): void {
    let newValue: number;
    const saleRatio = this.saleUnit()?.ratio || 1;

    if (saleRatio >= 1) {
      newValue = this.value + this.step();
    } else {
      newValue = this.value + (this.step() * (this.unit()?.ratio || 1)) / saleRatio;
      newValue = Number(newValue.toFixed(2));
    }

    this.valueUpdate(newValue);
  }

  decrement(): void {
    let newValue: number;
    const saleRatio = this.saleUnit()?.ratio || 1;

    if (saleRatio >= 1) {
      newValue = this.value - this.step();
    } else {
      newValue = this.value - (this.step() * (this.unit()?.ratio || 1)) / saleRatio;
      newValue = Number(newValue.toFixed(2));
    }

    if (newValue < 0) {
      return;
    }

    this.valueUpdate(newValue);
  }

  onEnterPress(): void {
    this.quantityInput.nativeElement.blur();
  }

  onFocus(): void {
    this.isInputFocused.set(true);
  }

  onBlur(): void {
    this.isInputFocused.set(false);
    this.valueUpdate(this.value);
  }

  private valueUpdate(value: number, callChangeEvent: boolean = true): void {
    this.currentValueSignal.set(value);
    this.setValue(value, callChangeEvent);
  }

  private guardFocused(value: number): boolean {
    if (this.zeroFocusEmitValue() || (!this.zeroFocusEmitValue() && !value)) {
      return this.isInputFocused() && value < this.max();
    }

    return false;
  }

  private setValue(value: number, callChangeEvent: boolean = true): void {
    if (this.guardFocused(value)) {
      return;
    }

    const validValue = Number(this.getValidCount(value).toFixed(2));

    this.quantityControl.setValue(validValue, {
      emitEvent: false,
    });
    this.currentValueSignal.set(validValue);

    this.valueExceeded.set(validValue >= this.max());

    if (callChangeEvent) {
      this.setSyncTimeout();
      this.onChange(validValue);
    }

    this.onTouched();
  }

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  private onChange(_: number): void {}

  private onTouched(): void {}

  private getValidCount(value: number): number {
    if (value < this.min() && !(value === 0 && this.allowZero())) {
      value = this.min() * this.step();
    }

    if (this.max() && value > this.max()) {
      value = this.max();
    }

    if ((value % this.step() !== 0 && this.selectedUnitIsSaleUnit()) || this.isStepCount()) {
      value = Math.ceil(value / this.step()) * this.step();
    }

    return value;
  }
}
